import { useEffect, useState } from "react";
import { useMutation, useQuery } from "convex/react";
import { Bell, Bot, Hash, Lock, Megaphone, Menu, ShieldCheck, Users } from "lucide-react";
import { api } from "../../../../convex/_generated/api";
import { Avatar } from "../ui/Avatar";
import { Button } from "../ui/button";
import { Composer } from "./Composer";
import { MembersPanel } from "./MembersPanel";
import { MessageList } from "./MessageList";
import { ThreadPanel } from "./ThreadPanel";
import { WebsiteLeads } from "./WebsiteLeads";
import { canViewWebsiteLeads } from "./websiteLeadsAccess";
import { conversationLabel, type Conversation as ConversationSummary, type MeReady } from "./types";

type Props = {
  me: MeReady;
  conversation: ConversationSummary;
  threadRootId: string | null;
  onOpenThread: (messageId: string | null) => void;
  onOpenSidebar: () => void;
  onOpenNotifications: () => void;
  onOpenConversation: (id: string, thread?: string | null) => void;
};

export function Conversation({ me, conversation, threadRootId, onOpenThread, onOpenSidebar, onOpenNotifications }: Props) {
  const participants = useQuery(api.communication.listParticipants, { conversationId: conversation.id });
  const typing = useQuery(api.communication.listTyping, { conversationId: conversation.id });
  const markRead = useMutation(api.communication.markRead);
  const [membersOpen, setMembersOpen] = useState(false);
  const [leadsRoom, setLeadsRoom] = useState<string | null>(null);
  const leadsAllowed = canViewWebsiteLeads(me.workspace.id, me.principalId, conversation.id);
  const leadsOpen = leadsAllowed && leadsRoom === conversation.id;
  useEffect(() => { setLeadsRoom(null); }, [conversation.id, me.principalId]);
  const label = conversationLabel(conversation);
  const isDirect = conversation.type === "direct";
  const isTeamFiles = !isDirect && label.toLocaleLowerCase() === "team-files";

  // Opening (or receiving while open) clears the unread badge.
  useEffect(() => {
    if (conversation.unreadCount > 0 && document.visibilityState === "visible") void markRead({ conversationId: conversation.id });
  }, [conversation.id, conversation.unreadCount, markRead]);

  const Icon = isDirect ? null : conversation.type === "private_channel" ? Lock : conversation.type === "announcement" ? Megaphone : Hash;
  const mentionables = (participants ?? []).filter((participant) => participant.principalId !== me.principalId);

  return (
    <>
      <header className="topbar">
        <div className="topbar__identity">
          <Button size="icon" variant="ghost" className="mobile-menu" aria-label="Open sidebar" onClick={onOpenSidebar}><Menu size={18} /></Button>
          {Icon !== null ? <span className="channel-icon"><Icon size={15} /></span> : <Avatar name={label} kind={conversation.partner?.kind} />}
          <div>
            <h1>{isDirect ? label : `#${label}`}{conversation.partner?.kind === "openclaw" ? <span className="agent-label"><Bot size={9} /> AI teammate</span> : null}</h1>
            <p>{isDirect ? (conversation.partner?.kind === "openclaw" ? "Direct message with your AI teammate — no @mention needed" : "Direct message") : conversation.topic ?? "No topic yet"}</p>
          </div>
        </div>
        <div className="topbar__actions">
          <Button size="icon" variant="ghost" className="mobile-notifications" aria-label="Notifications" title="Notifications" onClick={onOpenNotifications}><Bell size={18} /></Button>
          {!isDirect ? (
            <button type="button" className="classification-button" onClick={() => { setMembersOpen(true); }} title="Channel data level"><ShieldCheck size={13} /><span>{conversation.classification === "phi" ? "PHI" : conversation.classification}</span></button>
          ) : null}
          {!isDirect ? (
            <button type="button" className="members-button" onClick={() => { setMembersOpen((current) => !current); }} aria-expanded={membersOpen} aria-label={`Channel details, ${participants?.length ?? conversation.memberCount} members`} title="Channel details">
              <span className="stacked-avatars">{(participants ?? []).slice(0, 3).map((participant) => <Avatar key={participant.principalId} name={participant.displayName} kind={participant.kind} small />)}</span>
              <Users size={13} /> {participants?.length ?? conversation.memberCount}
            </button>
          ) : null}
        </div>
      </header>
      {leadsAllowed && <nav aria-label="Owner channel views" className="flex shrink-0 gap-2 border-b border-[var(--border)] bg-[var(--surface)] px-4 py-2">
        <Button style={{ minHeight: 44 }} variant={leadsOpen ? "ghost" : "secondary"} aria-pressed={!leadsOpen} onClick={() => { setLeadsRoom(null); }}>Messages</Button>
        <Button style={{ minHeight: 44 }} variant={leadsOpen ? "primary" : "secondary"} aria-pressed={leadsOpen} onClick={() => { setLeadsRoom(conversation.id); setMembersOpen(false); onOpenThread(null); }}>Website Leads</Button>
      </nav>}
      <div className="workspace-content">
        {leadsOpen ? <WebsiteLeads key={`${conversation.id}:${me.principalId}`} /> : <>
        <section className="conversation" aria-label={`Messages in ${label}`}>
          <MessageList me={me} conversation={conversation} mentionNames={(participants ?? []).map((participant) => participant.displayName)} onOpenThread={onOpenThread} />
          <div className="typing-row" aria-live="polite">{!isTeamFiles && typing !== undefined && typing.length > 0 ? `${typing.map((entry) => entry.displayName).join(", ")} ${typing.length === 1 ? "is" : "are"} typing…` : ""}</div>
          {participants === undefined ? null : <Composer conversation={conversation} mentionables={mentionables} placeholder={isTeamFiles ? "Add files or a note" : isDirect ? `Message ${label}` : `Message #${label}`} />}
        </section>
        {threadRootId !== null ? <ThreadPanel me={me} conversation={conversation} rootId={threadRootId} mentionables={mentionables} mentionNames={(participants ?? []).map((participant) => participant.displayName)} onClose={() => { onOpenThread(null); }} /> : null}
        {membersOpen && !isDirect ? <MembersPanel me={me} conversation={conversation} participants={participants ?? []} onClose={() => { setMembersOpen(false); }} /> : null}
        </>}
      </div>
    </>
  );
}
